import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, LayoutDashboard, LogOut, Menu, Settings, UserCircle, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "./ThemeToggle";
import { cn } from "@/lib/utils";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useLocale, intlLocale, toLocale, dbLocaleSuffix } from "@/lib/locale";

const PRIMARY_LINKS = [
  { key: "features", to: "/fonctionnalites" },
  { key: "pricing", to: "/tarifs" },
  { key: "showcase", to: "/vitrine" },
  { key: "resources", to: "/ressources" },
] as const;

const PERSONAS = [
  { key: "soloEditors", to: "/pour/editeurs-solo" },
  { key: "agenciesMedia", to: "/pour/agences-medias" },
  { key: "pressGroups", to: "/pour/groupes-presse" },
] as const;

export function MarketingHeader() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setSheetOpen(false);
  }, [location.pathname]);

  const today = new Intl.DateTimeFormat(intlLocale(toLocale(i18n.language)), {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date());

  const displayName = (user?.user_metadata?.full_name as string | undefined) || user?.email || t("public.account");
  const userInitial = displayName.charAt(0).toUpperCase();
  const avatarUrl = user?.user_metadata?.avatar_url as string | undefined;

  return (
    <header className="relative">
      <div className="border-b border-rule">
        <div className="mx-auto flex h-10 max-w-[1200px] items-center justify-between px-5">
          <div className="flex min-w-0 items-center gap-5">
            <span className="hidden truncate text-[11px] capitalize text-muted-foreground sm:inline">{today}</span>
            <Link to="/a-propos" className="hidden text-[11px] text-muted-foreground transition-colors hover:text-foreground md:inline">
              {t("public.about")}
            </Link>
            <Link to="/contact" className="hidden text-[11px] text-muted-foreground transition-colors hover:text-foreground md:inline">
              {t("public.contact")}
            </Link>
          </div>
          <div className="flex items-center gap-2">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex min-w-0 items-center gap-2 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                    aria-label={t("public.accountMenu")}
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary text-[10px] font-bold text-secondary-foreground">
                      {avatarUrl ? <img src={avatarUrl} alt="" className="h-full w-full rounded-full object-cover" /> : userInitial}
                    </span>
                    <span className="hidden max-w-[180px] truncate sm:inline">{displayName}</span>
                    <ChevronDown size={12} className="shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="min-w-0">
                    <span className="block truncate text-xs font-medium">{displayName}</span>
                    {user.email && user.email !== displayName && (
                      <span className="block truncate text-[11px] font-normal text-muted-foreground">{user.email}</span>
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer gap-2">
                      <LayoutDashboard className="h-4 w-4" />
                      {t("nav.dashboard")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="cursor-pointer gap-2">
                      <UserCircle className="h-4 w-4" />
                      {t("nav.profile")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="cursor-pointer gap-2">
                      <Settings className="h-4 w-4" />
                      {t("nav.settings")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => void signOut()} className="cursor-pointer gap-2 text-destructive focus:text-destructive">
                    <LogOut className="h-4 w-4" />
                    {t("nav.signOut")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </div>
      </div>

      <div
        className={cn(
          "sticky top-0 z-50 border-b border-rule bg-background/95 backdrop-blur transition-shadow",
          scrolled && "shadow-[0_4px_12px_-8px_rgba(0,0,0,0.25)]",
        )}
      >
        <div className="mx-auto flex max-w-[1200px] items-center justify-between gap-6 px-5 py-4">
          <Link to="/" className="flex flex-col leading-none">
            <span className="text-display text-2xl tracking-tight">delapresse</span>
            <span className="dateline hidden md:inline mt-1">{t("marketing.header.tagline")}</span>
          </Link>

          <NavigationMenu className="hidden lg:flex">
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link to="/fonctionnalites" className="px-3 py-2 text-sm font-medium text-foreground hover:text-accent-press transition-colors">
                    {t("marketing.header.nav.features")}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link to="/tarifs" className="px-3 py-2 text-sm font-medium text-foreground hover:text-accent-press transition-colors">
                    {t("marketing.header.nav.pricing")}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="text-sm font-medium">
                  {t("marketing.header.nav.forWho")}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[640px] grid-cols-3 gap-0 p-2">
                    {PERSONAS.map((p) => (
                      <li key={p.key}>
                        <NavigationMenuLink asChild>
                          <Link to={p.to} className="group block rounded p-4 transition-colors hover:bg-muted">
                            <span className="dateline mb-1 block">{t(`marketing.header.personas.${p.key}.dateline`)}</span>
                            <span className="text-display block text-lg tracking-tight">
                              {t(`marketing.header.personas.${p.key}.title`)}
                            </span>
                            <span className="mt-1 block text-xs text-muted-foreground">
                              {t(`marketing.header.personas.${p.key}.subtitle`)}
                            </span>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link to="/vitrine" className="px-3 py-2 text-sm font-medium text-foreground hover:text-accent-press transition-colors">
                    {t("marketing.header.nav.showcase")}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link to="/ressources" className="px-3 py-2 text-sm font-medium text-foreground hover:text-accent-press transition-colors">
                    {t("marketing.header.nav.resources")}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          <div className="flex items-center gap-2">
            {!user && (
              <Link
                to="/dpadmin"
                className="hidden h-9 items-center px-3 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground md:inline-flex"
              >
                {t("marketing.header.cta.signIn")}
              </Link>
            )}

            <Link
              to={user ? "/dashboard" : "/dpadmin?intent=signup"}
              className="hidden h-10 items-center bg-accent-press px-4 text-xs font-semibold uppercase tracking-wider text-accent-press-foreground transition-opacity hover:opacity-90 md:inline-flex"
            >
              {user ? t("nav.dashboard") : t("marketing.header.cta.startTrial")}
            </Link>

            <LanguageSwitcher compact className="hidden h-9 px-2 text-xs md:inline-flex" />
            <ThemeToggle className="hidden md:inline-flex" />

            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <button className="flex h-9 w-9 items-center justify-center lg:hidden" aria-label={t("marketing.header.menu.open")}>
                  {sheetOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full max-w-sm overflow-y-auto sm:max-w-sm">
                <SheetHeader className="border-b border-rule pb-4 text-left">
                  <SheetTitle className="text-display text-xl">delapresse</SheetTitle>
                </SheetHeader>

                <nav className="mt-4 flex flex-col">
                  {PRIMARY_LINKS.map((l) => (
                    <Link
                      key={l.key}
                      to={l.to}
                      className="border-b border-rule py-3 text-sm font-medium text-foreground hover:text-accent-press"
                    >
                      {t(`marketing.header.nav.${l.key}`)}
                    </Link>
                  ))}
                  <div className="border-b border-rule py-3">
                    <span className="dateline">{t("marketing.header.nav.forWho")}</span>
                    <ul className="mt-3 space-y-2 pl-2">
                      {PERSONAS.map((p) => (
                        <li key={p.key}>
                          <Link to={p.to} className="block text-sm text-foreground hover:text-accent-press">
                            {t(`marketing.header.personas.${p.key}.title`)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </nav>

                <div className="mt-6 flex flex-col gap-3">
                  {!user && (
                    <Link to="/dpadmin" className="text-sm text-muted-foreground hover:text-foreground">
                      {t("marketing.header.cta.signIn")}
                    </Link>
                  )}
                  <Link
                    to={user ? "/dashboard" : "/dpadmin?intent=signup"}
                    className="inline-flex h-11 items-center justify-center bg-accent-press px-4 text-xs font-semibold uppercase tracking-wider text-accent-press-foreground"
                  >
                    {user ? t("nav.dashboard") : t("marketing.header.cta.startTrial")}
                  </Link>
                  <div className="mt-2 flex items-center justify-between border-t border-rule pt-4">
                    <LanguageSwitcher compact className="h-8 px-2 text-xs" />
                    <ThemeToggle />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

      </div>
    </header>
  );
}
